import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import About from "./pages/About";
import Interests from "./pages/Interests";
import SillyProjects from "./pages/SillyProjects";
import Timer from "./pages/Timer";
import GamesWithTim from "./pages/GamesWithTim";
import BoardGameTracker from "./pages/BoardGameTracker";
import Keyboard from "./pages/Keyboard";
import MiniFreak from "./pages/MiniFreak";
import NumberSense from "./pages/NumberSense";
import NumberSenseV2 from "./pages/NumberSenseV2";
import SynthLab from "./pages/SynthLab";
import SynthBasics from "./pages/SynthBasics";
import SynthLabPractice from "./pages/SynthLabPractice";
import SynthLabPlayground from "./pages/SynthLabPlayground";
import SynthLabMidi from "./pages/SynthLabMidi";
import SewingPrimitives from "./pages/SewingPrimitives";
import SewingLab from "./pages/SewingLab";
import SewingLearn from "./pages/SewingLearn";
import SewingMake from "./pages/SewingMake";
import SewingCompositePage from "./pages/SewingCompositePage";
import SewingCreaturePage from "./pages/SewingCreaturePage";
import Manifest from "./pages/Manifest";
import ManifestLog from "./pages/ManifestLog";
import ManifestGate from "./components/ManifestGate";
import TokiPona from "./pages/TokiPona";
import KonNimi from "./pages/KonNimi";
import IloSitelen from "./pages/IloSitelen";
import SitelenNasin from "./pages/SitelenNasin";
import SitelenSike from "./pages/SitelenSike";
import ScaleCountry from "./pages/ScaleCountry";
import ScaleCountryPlace from "./pages/ScaleCountryPlace";
import ScaleCountryCampfire from "./pages/ScaleCountryCampfire";
import JianpuStudio from "./pages/JianpuStudio";
import JianpuPlay from "./pages/JianpuPlay";
import JianpuCompose from "./pages/JianpuCompose";
import JianpuChords from "./pages/JianpuChords";
import HomeKey from "./pages/HomeKey";
import HomeKeyLesson from "./pages/HomeKeyLesson";
import HomeKeyStudio from "./pages/HomeKeyStudio";
import HomeKeyPractice from "./pages/HomeKeyPractice";
import PianoDrills from "./pages/PianoDrills";
import CircleOfFifths from "./pages/CircleOfFifths";
import ChordCaller from "./pages/ChordCaller";
import ChordCraftHome from "./pages/ChordCraftHome";
import ChordCraftPalette from "./pages/ChordCraftPalette";
import ChordCraftGym from "./pages/ChordCraftGym";
import ChordCraftSlither from "./pages/ChordCraftSlither";
import ChordCraftLoop from "./pages/ChordCraftLoop";
import StarterTapeHub from "./pages/StarterTapeHub";
import StarterTapeTape from "./pages/StarterTapeTape";
import StarterTapeSandbox from "./pages/StarterTapeSandbox";
import StarterTapeSpaceSandbox from "./pages/StarterTapeSpaceSandbox";
import NotFound from "./pages/NotFound";

// The /manifest store, the /o-kama-sona toki pona trainer, and the
// /scale-country field guide are self-contained sub-sites with their own
// full-screen look — they render without the navbar.
const AppShell = () => {
  const location = useLocation();
  const hideNavbar =
    location.pathname.startsWith("/manifest") ||
    location.pathname.startsWith("/o-kama-sona") ||
    location.pathname.startsWith("/kon-nimi") ||
    location.pathname.startsWith("/ilo-sitelen") ||
    location.pathname.startsWith("/sitelen-nasin") ||
    location.pathname.startsWith("/sitelen-sike") ||
    location.pathname.startsWith("/number-sense") ||
    location.pathname.startsWith("/scale-country") ||
    location.pathname.startsWith("/home-key") ||
    location.pathname.startsWith("/jianpu") ||
    location.pathname.startsWith("/piano-drills") ||
    location.pathname.startsWith("/circle-of-fifths") ||
    location.pathname.startsWith("/chord-caller") ||
    location.pathname.startsWith("/chord-craft") ||
    location.pathname.startsWith("/starter-tape");

  return (
    <>
      {!hideNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/interests" element={<Interests />} />
        {/* Old URL for the interests page — keep stray links working. */}
        <Route path="/boardgames" element={<Navigate to="/interests" replace />} />
        <Route path="/sillyprojects" element={<SillyProjects />} />
        <Route path="/timer" element={<Timer />} />
        {/* Unlisted pages (not in the navbar) — direct link only. */}
        <Route path="/games-with-tim" element={<GamesWithTim />} />
        <Route path="/board-game-tracker" element={<BoardGameTracker />} />
        <Route path="/keyboard" element={<Keyboard />} />
        <Route path="/minifreak" element={<MiniFreak />} />
        <Route path="/number-sense" element={<NumberSense />} />
        <Route path="/number-sense-v2" element={<NumberSenseV2 />} />
        <Route path="/synth-lab" element={<SynthLab />} />
        <Route path="/synth-lab/learn" element={<SynthBasics />} />
        <Route path="/synth-lab/practice" element={<SynthLabPractice />} />
        <Route path="/synth-lab/playground" element={<SynthLabPlayground />} />
        <Route path="/synth-lab/midi" element={<SynthLabMidi />} />
        <Route path="/sewing-primitives" element={<SewingPrimitives />} />
        <Route path="/sewing-primitives/lab" element={<SewingLab />} />
        <Route path="/sewing-primitives/learn" element={<SewingLearn />} />
        <Route path="/sewing-primitives/make" element={<SewingMake />} />
        <Route
          path="/sewing-primitives/make/:compositeId"
          element={<SewingCompositePage />}
        />
        <Route path="/sewing-primitives/creature" element={<SewingCreaturePage />} />
        <Route path="/o-kama-sona" element={<TokiPona />} />
        <Route path="/kon-nimi" element={<KonNimi />} />
        <Route path="/ilo-sitelen" element={<IloSitelen />} />
        <Route path="/sitelen-nasin" element={<SitelenNasin />} />
        <Route path="/sitelen-sike" element={<SitelenSike />} />
        <Route path="/scale-country" element={<ScaleCountry />} />
        <Route path="/scale-country/campfire" element={<ScaleCountryCampfire />} />
        <Route path="/scale-country/:placeId" element={<ScaleCountryPlace />} />
        <Route path="/jianpu" element={<JianpuStudio />} />
        <Route path="/jianpu/play/:songId" element={<JianpuPlay />} />
        <Route path="/jianpu/compose" element={<JianpuCompose />} />
        <Route path="/jianpu/chords" element={<JianpuChords />} />
        <Route path="/home-key" element={<HomeKey />} />
        <Route path="/home-key/lesson/:lessonId" element={<HomeKeyLesson />} />
        <Route path="/home-key/studio" element={<HomeKeyStudio />} />
        <Route path="/home-key/practice" element={<HomeKeyPractice />} />
        <Route path="/piano-drills" element={<PianoDrills />} />
        <Route path="/circle-of-fifths" element={<CircleOfFifths />} />
        <Route path="/chord-caller" element={<ChordCaller />} />
        <Route path="/chord-craft" element={<ChordCraftHome />} />
        <Route path="/chord-craft/palette" element={<ChordCraftPalette />} />
        <Route path="/chord-craft/gym" element={<ChordCraftGym />} />
        <Route path="/chord-craft/slither" element={<ChordCraftSlither />} />
        <Route path="/chord-craft/loop" element={<ChordCraftLoop />} />
        <Route path="/starter-tape" element={<StarterTapeHub />} />
        <Route path="/starter-tape/tape/:tapeId" element={<StarterTapeTape />} />
        <Route path="/starter-tape/sandbox" element={<StarterTapeSandbox />} />
        <Route
          path="/starter-tape/sandbox/deep-space"
          element={<StarterTapeSpaceSandbox />}
        />
        <Route
          path="/manifest"
          element={
            <ManifestGate>
              <Manifest />
            </ManifestGate>
          }
        />
        <Route
          path="/manifest/log"
          element={
            <ManifestGate>
              <ManifestLog />
            </ManifestGate>
          }
        />
        {/* Whimsical catch-all for any stray URL. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Analytics />
    </>
  );
};

const App = () => {
  return (
    <Router>
      <AppShell />
    </Router>
  );
};

export default App;
